import { useMemo, useState } from 'react';

import { gruppenkennzeichenApi } from '@/api/services/gruppenkennzeichenApi';
import { DropDownList, EmptyState } from '@/components/ui';
import { EntityTypes } from '@/types/enums';

import { AdminGruppenkennzeichenList } from './lists/AdminGruppenkennzeichenList';
import { useAdminList } from './lists/adminList';
import { MasterDetail, type MasterItem } from './MasterDetail';

/**
 * Group flags — Port von `IwmAdminGruppenkennzeichen.razor` (REQ-29 §3).
 *
 * Statt dreier Menüspalten ineinander (Entität → Feld → Werte) steht links eine Liste aus
 * Entität und Feld, rechts die Werte des gewählten Feldes.
 */
interface AdminEntity {
  value: EntityTypes;
  /** Anzeigename wie heute — nicht `entityTypeDisplayNames`. */
  name: string;
}

/**
 * Fest verdrahtete Entitätsliste aus `FetchEntities()` — bewusst **nicht** aus
 * `EntityTypes` abgeleitet.
 */
const ENTITIES: readonly AdminEntity[] = [
  { value: EntityTypes.Bestellposition, name: 'OrderPosition' },
  { value: EntityTypes.Container, name: 'Container' },
  { value: EntityTypes.Rechnung, name: 'Receipt' },
];

/** Eine Kombination aus Entität und Feld — ein Eintrag der Liste links. */
interface FlagTarget {
  key: string;
  entity: EntityTypes;
  entityName: string;
  field: string;
  count: number;
}

/**
 * Die Feldnamen kommen wie heute aus `getFieldNames(entity)` — die API bildet sie über
 * `GroupBy(x => x.Fieldname)` aus den vorhandenen Werten. Die Anzahl je Feld steht in
 * derselben Tabelle und wird mit einem Aufruf für alle Felder geholt.
 */
const load = async (): Promise<FlagTarget[]> => {
  const [all, fields] = await Promise.all([
    gruppenkennzeichenApi.get(),
    Promise.all(
      ENTITIES.map(async (entity) => ({
        entity,
        names: await gruppenkennzeichenApi.getFieldNames(entity.value),
      })),
    ),
  ]);

  return fields.flatMap(({ entity, names }) =>
    names.map((field) => ({
      key: `${String(entity.value)}|${field}`,
      entity: entity.value,
      entityName: entity.name,
      field,
      count: all.filter((row) => row.Entitiy === entity.value && row.Fieldname === field).length,
    })),
  );
};

interface EntityOption {
  value: number;
  text: string;
  entity: EntityTypes | null;
}

const ALL_ENTITIES: EntityOption = { value: -1, text: 'Alle Entitäten', entity: null };
const ENTITY_OPTIONS: EntityOption[] = [
  ALL_ENTITIES,
  ...ENTITIES.map((entity) => ({ value: entity.value, text: entity.name, entity: entity.value })),
];
const ENTITY_FIELDS = { value: 'value', text: 'text' } as const;

export function AdminGruppenkennzeichen() {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [entity, setEntity] = useState<EntityTypes | null>(null);
  const { rows, loading, error, reload } = useAdminList<FlagTarget>(load);

  const items = useMemo<MasterItem<string>[]>(
    () =>
      rows
        .filter((target) => entity === null || target.entity === entity)
        .map((target) => ({
          id: target.key,
          label: `${target.entityName} · ${target.field}`,
          // Gesucht wird über den Feldnamen.
          searchText: target.field,
          meta: String(target.count),
        })),
    [entity, rows],
  );

  const selected = rows.find((target) => target.key === selectedKey) ?? null;

  return (
    <MasterDetail<string>
      listLabel="Group flags"
      items={items}
      selectedId={selectedKey}
      onSelect={setSelectedKey}
      loading={loading}
      error={error}
      onRetry={() => {
        void reload();
      }}
      searchPlaceholder="Feldname"
      emptyText="Keine Group flags"
      wide
      filter={
        <DropDownList<number, EntityOption>
          value={entity ?? ALL_ENTITIES.value}
          onChange={(_value, item) => {
            setEntity(item?.entity ?? null);
          }}
          items={ENTITY_OPTIONS}
          fields={ENTITY_FIELDS}
          label="Entität"
        />
      }
    >
      {selected === null ? (
        <EmptyState
          iconClass="fa-solid fa-tags"
          title="Kein Feld gewählt"
          description="Wählen Sie links eine Entität mit Feld aus."
        />
      ) : (
        <AdminGruppenkennzeichenList
          // Der Wechsel des Feldes beginnt mit einer leeren Zeile, nicht mit dem Entwurf des
          // vorigen Feldes.
          key={selected.key}
          entity={selected.entity}
          entityName={selected.entityName}
          field={selected.field}
          onChanged={() => {
            void reload();
          }}
        />
      )}
    </MasterDetail>
  );
}

export default AdminGruppenkennzeichen;
